
import { useEffect, useState } from "react";

/**
 * Returns `false` on the first render and flips to `true` after the given
 * delay (default 150ms, matching the PageTransition fade-in duration).
 *
 * Used to split a page's shell (toolbar / filters / header) from its heavy
 * body (MobX-driven DataTable over 6k+ rows) so the shell paints in the same
 * frame as the route transition and the heavy content fills in once the
 * fade-in has largely completed. Without this, React commits shell + table
 * in a single synchronous batch and the tab switch feels stalled; with too
 * short a delay, the skeleton and heavy content animation collide and the
 * content pops into the fade mid-flight.
 *
 * A delay of 0 still returns on the next macrotask (guaranteeing one paint
 * before mount), but higher delays wait for the page transition to settle.
 * 150ms lines up with the `duration-200` PageTransition: the fade finishes
 * just as the heavy body swaps in, which reads as a single smooth motion
 * instead of a two-step pop.
 */
export function useDeferredMount(delay = 150): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setReady(true), delay);
    return () => clearTimeout(id);
  }, [delay]);
  return ready;
}
